import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HelmetProvider } from "react-helmet-async";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/hooks/useAuth";
import { SessionTimeoutProvider } from "@/components/SessionTimeoutProvider";
import ProtectedRoute from "@/components/ProtectedRoute";
import AdminRoute from "@/components/AdminRoute";

// Eagerly load the landing page (first paint)
import Index from "./pages/Index.tsx";

// Lazy-load all other routes to reduce initial bundle
const AuthPage = lazy(() => import("./pages/AuthPage.tsx"));
const Dashboard = lazy(() => import("./pages/Dashboard.tsx"));
const CreateEvent = lazy(() => import("./pages/CreateEvent.tsx"));
const EditEvent = lazy(() => import("./pages/EditEvent.tsx"));
const Occasions = lazy(() => import("./pages/Occasions.tsx"));
const Templates = lazy(() => import("./pages/Templates.tsx"));
const Gifts = lazy(() => import("./pages/Gifts.tsx"));
const Cards = lazy(() => import("./pages/Cards.tsx"));
const Contacts = lazy(() => import("./pages/Contacts.tsx"));
const Settings = lazy(() => import("./pages/Settings.tsx"));
const AdminPanel = lazy(() => import("./pages/AdminPanel.tsx"));
const ResetPassword = lazy(() => import("./pages/ResetPassword.tsx"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy.tsx"));
const TermsOfUse = lazy(() => import("./pages/TermsOfUse.tsx"));
const RefundPolicy = lazy(() => import("./pages/RefundPolicy.tsx"));
const ContactUs = lazy(() => import("./pages/ContactUs.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));
const GroupSend = lazy(() => import("./pages/GroupSend.tsx"));
const CheckoutReturn = lazy(() => import("./pages/CheckoutReturn.tsx"));
const Guides = lazy(() => import("./pages/Guides.tsx"));
const GuideArticle = lazy(() => import("./pages/GuideArticle.tsx"));
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";

const queryClient = new QueryClient();

const PageFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin" />
  </div>
);

const App = () => (
  <HelmetProvider>
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem disableTransitionOnChange={false}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <SessionTimeoutProvider />
          <PaymentTestModeBanner />
          <Suspense fallback={<PageFallback />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/login" element={<AuthPage />} />
              <Route path="/signup" element={<AuthPage />} />
              <Route path="/checkout/return" element={<CheckoutReturn />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfUse />} />
              <Route path="/refund-policy" element={<RefundPolicy />} />
              <Route path="/contact" element={<ContactUs />} />
              <Route path="/guides" element={<Guides />} />
              <Route path="/guides/:slug" element={<GuideArticle />} />
              <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/occasions/new" element={<ProtectedRoute><CreateEvent /></ProtectedRoute>} />
              <Route path="/occasions/:id/edit" element={<ProtectedRoute><EditEvent /></ProtectedRoute>} />
              <Route path="/occasions" element={<ProtectedRoute><Occasions /></ProtectedRoute>} />
              <Route path="/templates" element={<ProtectedRoute><Templates /></ProtectedRoute>} />
              <Route path="/gifts" element={<ProtectedRoute><Gifts /></ProtectedRoute>} />
              <Route path="/cards" element={<ProtectedRoute><Cards /></ProtectedRoute>} />
              <Route path="/group-send" element={<ProtectedRoute><GroupSend /></ProtectedRoute>} />
              <Route path="/contacts" element={<ProtectedRoute><Contacts /></ProtectedRoute>} />
              <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
              <Route path="/admin" element={<ProtectedRoute><AdminRoute><AdminPanel /></AdminRoute></ProtectedRoute>} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
  </HelmetProvider>
);

export default App;
